/**
 * Lớp gọi API của mốc M5 — bám hợp đồng docs/04 §4.6.
 * Kiểu dữ liệu đặt tại đây để không đụng `lib/types.ts` (vùng dùng chung).
 */

import { api } from '../../lib/api';
import { initialsOf } from '../../lib/format';
import type { UserListItem as AdminUserListItem } from '../admin/types';

export interface UserBrief {
  id: string;
  fullName: string;
  email: string;
  avatarUrl: string | null;
  initials: string;
}

export interface DomainRoleBrief {
  id: string;
  slug: string;
  name: string;
}

export type GrantRelation = 'owned' | 'granted' | 'viewall';

export interface GrantView {
  id: string | null;
  user: UserBrief;
  domainRole: DomainRoleBrief | null;
  relation: GrantRelation;
  isOwner: boolean;
  grantedBy: UserBrief | null;
  createdAt: string | null;
  revocable: boolean;
}

export interface PendingGrantView {
  id: string;
  email: string;
  domainRole: DomainRoleBrief | null;
  invitedBy: { id: string; fullName: string; email: string } | null;
  message: string | null;
  expiresAt: string | null;
  createdAt: string | null;
}

export interface UserDomainAccessView {
  grantId: string | null;
  domain: { id: string; name: string; recordCount: number; status: string };
  domainRole: DomainRoleBrief | null;
  relation: GrantRelation;
  isOwner: boolean;
  grantedBy: UserBrief | null;
  createdAt: string | null;
  revocable: boolean;
}

export interface PermissionUserView {
  id: string;
  fullName: string;
  email: string;
  avatarUrl: string | null;
  initials: string;
  status: string;
  role: { id: string; slug: string; name: string };
  domainCount: number;
}

export interface PermissionDomainView {
  id: string;
  name: string;
  status: string;
  recordCount: number;
  owner: UserBrief | null;
  addedByMe: boolean;
  memberCount: number;
}

export interface AssignableDomainRoleView {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  assignable: boolean;
}

export interface UserLookupView {
  found: boolean;
  user: UserBrief | null;
  alreadyGranted: boolean;
  currentDomainRole: DomainRoleBrief | null;
}

export interface DomainGrantsView {
  items: GrantView[];
  pending: PendingGrantView[];
}

export type InviteResult =
  | { mode: 'granted'; grant: GrantView; pending: null }
  | { mode: 'invited'; grant: null; pending: PendingGrantView };

/* ── Khoá cache dùng chung — FR-078: hai tab cùng một nguồn ────────── */

export const permKeys = {
  all: ['permissions'] as const,
  users: (q: string) => ['permissions', 'users', q] as const,
  domains: (q: string) => ['permissions', 'domains', q] as const,
  domainRoles: ['permissions', 'domain-roles'] as const,
  userDomains: (uid: string) => ['permissions', 'user-domains', uid] as const,
  grantableDomains: (uid: string) => ['permissions', 'grantable-domains', uid] as const,
  domainGrants: (id: string) => ['permissions', 'domain-grants', id] as const,
};

/* ── Hàm gọi ──────────────────────────────────────────────────────── */

export const permApi = {
  /*
   * OQ-051 gộp nguồn: `GET /users` (M6) nay trả `accessibleDomainCount` nên cột
   * trái tab *Theo người dùng* không cần endpoint phụ `/permissions/users` nữa.
   * Cùng capability `adm.users`, cùng phân trang — chỉ khác chỗ `initials` do
   * FE tự tính (`initialsOf`), y như `UsersPage` vẫn làm.
   */
  users: async (q: string): Promise<PermissionUserView[]> => {
    const page = await api.get<{ items: AdminUserListItem[] }>(
      `/users?perPage=100${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    );
    return page.items.map((row) => ({
      id: row.id,
      fullName: row.fullName,
      email: row.email,
      avatarUrl: row.avatarUrl,
      initials: initialsOf(row.fullName, row.email),
      status: row.status,
      role: row.role,
      domainCount: row.accessibleDomainCount,
    }));
  },
  domains: (q: string) =>
    api.get<PermissionDomainView[]>(
      `/permissions/domains${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  domainRoles: () => api.get<AssignableDomainRoleView[]>('/permissions/domain-roles'),

  userDomains: (uid: string) => api.get<UserDomainAccessView[]>(`/users/${uid}/domains`),
  grantableDomains: (uid: string) =>
    api.get<PermissionDomainView[]>(`/users/${uid}/domains?scope=grantable`),

  domainGrants: (domainId: string) => api.get<DomainGrantsView>(`/domains/${domainId}/grants`),

  lookup: (email: string, domainId?: string) =>
    api.get<UserLookupView>(
      `/users/lookup?email=${encodeURIComponent(email)}${domainId ? `&domainId=${domainId}` : ''}`,
    ),

  createGrant: (
    domainId: string,
    body: { userId?: string; email?: string; domainRoleId: string; notify?: boolean },
  ) => api.post<GrantView>(`/domains/${domainId}/grants`, body),

  updateGrant: (domainId: string, grantId: string, domainRoleId: string) =>
    api.patch<GrantView>(`/domains/${domainId}/grants/${grantId}`, { domainRoleId }),

  revokeGrant: (domainId: string, grantId: string) =>
    api.del<void>(`/domains/${domainId}/grants/${grantId}`),

  invite: (
    domainId: string,
    body: {
      email: string;
      domainRoleId: string;
      displayName?: string | null;
      message?: string | null;
      expiresInDays?: number;
    },
  ) => api.post<InviteResult>(`/domains/${domainId}/invitations`, body),

  revokeInvite: (domainId: string, pendingId: string) =>
    api.del<void>(`/domains/${domainId}/invitations/${pendingId}`),
};

/** Định dạng email hợp lệ tối thiểu — chặn gọi API vô ích (FR-080). */
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
